import PropTypes from 'prop-types';
import { Box, Typography, Paper, Grid, Chip } from '@mui/material';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
    CurrencyRupee,
    AccountBalanceWallet,
    Payments,
    AdminPanelSettingsRounded,
    StorefrontRounded,
    InsightsRounded
} from '@mui/icons-material';

const getIcon = (role) => {
    const lowerRole = role?.toLowerCase() || '';
    if (lowerRole.includes('agent')) return Payments;
    if (lowerRole.includes('distributor')) return StorefrontRounded;
    if (lowerRole.includes('revenue') || lowerRole.includes('profit')) return InsightsRounded;
    if (lowerRole.includes('admin') || lowerRole.includes('manage')) return AdminPanelSettingsRounded;
    if (lowerRole.includes('bc')) return AccountBalanceWallet;
    return CurrencyRupee;
};

const getColor = (role) => {
    const lowerRole = role?.toLowerCase() || '';
    if (lowerRole.includes('agent')) return '#38bdf8'; // Sky blue
    if (lowerRole.includes('distributor')) return '#818cf8'; // Indigo
    if (lowerRole.includes('revenue')) return '#34d399'; // Emerald
    if (lowerRole.includes('profit')) return '#fbbf24'; // Amber
    if (lowerRole.includes('merchant')) return '#f472b6'; // Pink
    return '#94a3b8'; // Slate
};

const AccountCard = ({ item, index }) => {
    console.log(item);
    const Icon = getIcon(item.role || item.name);
    const color = getColor(item.role);

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.08 }}
            style={{ height: '100%' }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: '24px',
                    border: '1px solid',
                    borderColor: 'divider',
                    position: 'relative',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 24px 48px -12px ${color}25`,
                        borderColor: color,
                        '& .icon-box': {
                            transform: 'scale(1.1) rotate(-8deg)',
                            bgcolor: color,
                            color: '#fff'
                        }
                    }
                }}
            >
                {/* Background Accent */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -30,
                        right: -30,
                        width: 140,
                        height: 140,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${color}20 0%, ${color}05 100%)`,
                        filter: 'blur(20px)'
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box
                        className="icon-box"
                        sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '16px',
                            bgcolor: `${color}12`,
                            color: color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.4s ease',
                            boxShadow: `0 8px 16px -4px ${color}15`,
                            position: 'relative',
                            zIndex: 1
                        }}
                    >
                        <Icon sx={{ fontSize: 30 }} />
                    </Box>
                    <Box sx={{ textAlign: 'right', position: 'relative', zIndex: 1 }}>
                        <Typography
                            variant="overline"
                            sx={{
                                color: color,
                                fontWeight: 900,
                                letterSpacing: '0.15em',
                                fontSize: '0.65rem',
                                display: 'block',
                                mb: 0.5
                            }}
                        >
                            {item.name}
                        </Typography>
                        <Chip
                            label={item.type || 'ACCOUNT'}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.6rem',
                                fontWeight: 800,
                                bgcolor: `${color}10`,
                                color: color,
                                border: `1px solid ${color}20`
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant="h5"
                        fontWeight={800}
                        color="text.primary"
                        sx={{
                            mb: 0.5,
                            letterSpacing: '-0.02em',
                            textTransform: 'capitalize'
                        }}
                    >
                        {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Consolidated net position
                    </Typography>
                </Box>

                <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ mb: 2.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={800}
                            sx={{ letterSpacing: '0.05em', display: 'block', mb: 0.5 }}
                        >
                            NET AMOUNT
                        </Typography>
                        <Typography
                            variant="h2"
                            fontWeight={900}
                            sx={{
                                color: item.amount >= 0 ? 'success.main' : 'error.main',
                                letterSpacing: '-0.03em',
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: 0.5
                            }}
                        >
                            <Typography variant="h4" component="span" fontWeight={900} sx={{ opacity: 0.8 }}>
                                ₹
                            </Typography>
                            <CountUp end={item.amount} separator="," duration={2} decimals={2} />
                        </Typography>
                    </Box>

                    <Grid container spacing={1.5} sx={{ pt: 2, borderTop: '1px solid', borderColor: 'grey.50' }}>
                        <Grid item xs={6}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.2 }}>
                                    DEBIT
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                                    ₹{item.debit?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6}>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 0.2 }}>
                                    CREDIT
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                                    ₹{item.credit?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </motion.div>
    );
};

AccountCard.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
        debit: PropTypes.number,
        credit: PropTypes.number,
        type: PropTypes.string,
        role: PropTypes.string
    }).isRequired,
    index: PropTypes.number.isRequired
};

export default AccountCard;
